// IMPORTS

// external
import { bytesToBigInt } from "algosdk"

// global
import {
  FIXED_18_SCALE_FACTOR,
  SECONDS_PER_YEAR
} from "../../globals"
import AssetAmount from "../../assetData/assetAmount"

// local
import { STAKING_STRINGS } from "./stakingConfig"
import Staking from "./staking"

export default class RewardsProgramState {
  staking: Staking
  rewardsProgramIndex: number
  rewardsProgramCounter: number
  rewardsAssetId: number
  rewardsPerSecond: number
  rewardsCoefficient: bigint
  rewardsIssued: number
  rewardsPayed: number

  projectedRewardsCoefficient: bigint
  
  baseAPR: number
  maxBoostedAPR: number
  
  annualBaseRewardsPer1k: number
  maxBoostedAnnualRewardsPer1k: number

  /**
   * Constructor for rewards program state
   * 
   * @param stakingState - formatted staking state
   * @param rewardsProgramIndex - index of rewards program 
   */
  constructor(staking, stakingState: {}, rewardsProgramIndex: number) {
    this.staking = staking
    this.rewardsProgramIndex = rewardsProgramIndex
    this.rewardsProgramCounter =
      stakingState[STAKING_STRINGS.rewards_program_counter_prefix + this.rewardsProgramIndex.toString()] || 0
    this.rewardsAssetId =
      stakingState[STAKING_STRINGS.rewards_asset_id_prefix + this.rewardsProgramIndex.toString()] || 0
    this.rewardsPerSecond =
      stakingState[STAKING_STRINGS.rewards_per_second_prefix + this.rewardsProgramIndex.toString()] || 0
    this.rewardsIssued = stakingState[STAKING_STRINGS.rewards_issued_prefix + this.rewardsProgramIndex.toString()] || 0
    this.rewardsPayed = stakingState[STAKING_STRINGS.rewards_payed_prefix + this.rewardsProgramIndex.toString()] || 0

    const nonFormattedRewardsCoefficient =
      stakingState[STAKING_STRINGS.rewards_coefficient_prefix + this.rewardsProgramIndex.toString()] || 0
    if (nonFormattedRewardsCoefficient === 0) {
      this.rewardsCoefficient = BigInt(0)
    } else {
      const bytesVer = new Uint8Array(Buffer.from(nonFormattedRewardsCoefficient, "base64"))
      const bigInt = bytesToBigInt(bytesVer)
      this.rewardsCoefficient = bigInt
    }

    if (staking.scaledTotalStaked > 0) {
      this.projectedRewardsCoefficient = this.rewardsCoefficient +
        BigInt((Math.floor(Date.now() / 1000) - staking.latestTime) * this.rewardsPerSecond) * FIXED_18_SCALE_FACTOR / BigInt(staking.scaledTotalStaked)
    } else {
      this.projectedRewardsCoefficient = this.rewardsCoefficient
    }
  }

  async loadState() {
    let rawRewardsPerYear = this.staking.assetDataClient.getAsset(this.rewardsPerSecond * SECONDS_PER_YEAR, this.rewardsAssetId)
    let rewardsPerYear = rawRewardsPerYear.toDisplayAmount()
    let rewardsPerYearUSD = rawRewardsPerYear.toUSD()
    let totalScaledStakedUSD = this.staking.getScaledTotalStaked().toUSD()
    if (totalScaledStakedUSD > 0) {
      this.baseAPR = 0.4 * rewardsPerYearUSD / totalScaledStakedUSD
      this.maxBoostedAPR = rewardsPerYearUSD / totalScaledStakedUSD
      this.annualBaseRewardsPer1k = 0.4 * rewardsPerYear / (totalScaledStakedUSD / 1000)
      this.maxBoostedAnnualRewardsPer1k = rewardsPerYear / (totalScaledStakedUSD / 1000)
    } else {
      this.baseAPR = 0
      this.maxBoostedAPR = 0
      this.annualBaseRewardsPer1k = 0
      this.maxBoostedAnnualRewardsPer1k = 0
    }
  }

  // DEPRECATED
  getAPR(): number {
    return this.baseAPR;
    let rewardsPerYear = this.staking.assetDataClient.getAsset(this.rewardsPerSecond * SECONDS_PER_YEAR, this.rewardsAssetId)
    return rewardsPerYear.toUSD() / (this.staking.getTotalStaked().toUSD() || 1)
  }
}

export class UserRewardsProgramState {
  staking: Staking
  rewardsProgramIndex: number
  userRewardsProgramCounter: number
  userRewardsCoefficient: bigint
  userUnclaimedRewards: number
  userUnrealizedRewards: number

  userAPR: number
  userRewardsPerDay: AssetAmount

  /**
   * Constructor for user rewards program state object
   * 
   * @param formattedUserLocalState - local state for the user
   * @param rewardsProgramIndex - index of the rewards program
   * @param staking - staking
   * @param userScaledTotalStaked - scaled user total staked
   */
  constructor(
    formattedUserLocalState: {},
    rewardsProgramIndex: number,
    staking: Staking,
    userScaledTotalStaked: number,
    userRewardsBoost: number
  ) {
    this.staking = staking
    this.rewardsProgramIndex = rewardsProgramIndex
    this.userRewardsProgramCounter =
      formattedUserLocalState[
        STAKING_STRINGS.user_rewards_program_counter_prefix + this.rewardsProgramIndex.toString()
      ] || 0
    this.userUnclaimedRewards =
      formattedUserLocalState[STAKING_STRINGS.user_unclaimed_rewards_prefix + this.rewardsProgramIndex.toString()] || 0

    const nonFormattedRewardsCoefficient =
      formattedUserLocalState[STAKING_STRINGS.user_rewards_coefficient_prefix + this.rewardsProgramIndex.toString()] || 0

    if (nonFormattedRewardsCoefficient === 0) {
      this.userRewardsCoefficient = BigInt(0)
    } else {
      const bytesVer = new Uint8Array(Buffer.from(nonFormattedRewardsCoefficient, "base64"))
      const bigInt = bytesToBigInt(bytesVer)
      this.userRewardsCoefficient = bigInt
    }

    const rewardsProgram = staking.rewardsProgramStates[this.rewardsProgramIndex]

    this.userRewardsPerDay = staking.assetDataClient.getAsset(
      rewardsProgram.rewardsPerSecond * 86400 * userScaledTotalStaked / staking.scaledTotalStaked,
      rewardsProgram.rewardsAssetId)

    // calc user unrealized rewards (global coefficient on rewards program - user rewards coefficient on rewards program) * userTotalScaledStaked
    this.userUnrealizedRewards = Number(
      ((rewardsProgram.projectedRewardsCoefficient - this.userRewardsCoefficient) * BigInt(userScaledTotalStaked) / FIXED_18_SCALE_FACTOR) + BigInt(this.userUnclaimedRewards)
    )
    
    // calc user APRs
    this.userAPR = rewardsProgram.baseAPR * userRewardsBoost
  }
}
